import { DayJsUtils } from '@hidat/huijs-dates';
import {
  convert,
  LocalDate,
  LocalDateTime,
  LocalTime,
  ZonedDateTime,
  ZoneId,
  ZoneOffset,
} from '@js-joda/core';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
dayjs.extend(objectSupport);

/**
 * Joda Date Utils
 * Extension to DayJs utils for managing dates
 */
export class JDateUtils extends DayJsUtils {
  /**
   * To Date Only String
   * Converts the given date type to a date only string - i.e., YYYY-MM-DD.
   * This is primarily meant for serialization.
   * @param val
   */
  static toLocalDateString(val: unknown): string | undefined {
    let res: string | undefined;
    if (val) {
      if (!(val instanceof LocalDate)) {
        val = this.toLocalDate(val);
      }

      if (val instanceof LocalDate) {
        res = val.toString();
      } else {
        throw new Error('Invalid type passed to toDateString');
      }
    }
    return res;
  }

  /**
   * To Date With Zone String
   * The goal of the Zoned Date is to serialize with the date that the user entered into some form (they don't care about timezones...),
   * carry this across the wire with that date, but then let the receiving system (i.e., API) convert it to a UTC datetime.
   * This allows us to just store the date in a URL string and be smart enough to deserialize it properly.
   * @param val
   */
  static toDateWithZoneStr(val: unknown): string | undefined {
    let res: string | undefined;
    if (val) {
      if (val instanceof Date) {
        const offset = -val.getTimezoneOffset(); // thankyou JS for making this the opposite of what you would expect....
        val = ZonedDateTime.of(
          val.getFullYear(),
          val.getMonth() + 1,
          val.getDate(),
          0,
          0,
          0,
          0,
          ZoneOffset.ofTotalMinutes(offset),
        );
      } else if (dayjs.isDayjs(val)) {
        const m = val as dayjs.Dayjs;
        val = ZonedDateTime.of(
          m.year(),
          m.month() + 1,
          m.date(),
          0,
          0,
          0,
          0,
          ZoneOffset.ofTotalMinutes(m.utcOffset()),
        );
      } else if (val instanceof LocalDate) {
        val = ZonedDateTime.of(
          val,
          LocalTime.MIDNIGHT,
          ZoneOffset.systemDefault(),
        ).withFixedOffsetZone();
      }

      if (val instanceof ZonedDateTime) {
        res = val
          .withHour(0)
          .withMinute(0)
          .withSecond(0)
          .withNano(0)
          .toString();
      } else {
        throw new Error('Invalid type passed to toDateWithZoneStr');
      }
    }
    return res;
  }

  /**
   * To LocalDate
   * Converts the given val to a joda LocalDate.
   * @param val
   */
  static toLocalDate(val: unknown): LocalDate | undefined {
    let res: LocalDate | undefined;
    if (val) {
      if (val instanceof LocalDate) {
        res = val;
      } else if (typeof val === 'string') {
        try {
          if (val.length > 10) {
            val = val.substr(0, 10);
          }
          res = LocalDate.parse(val as string);
        } catch (e) {
          console.error('Error parsing Date for: ', val);
          console.warn((e as Error).toString());
        }
      } else if (dayjs.isDayjs(val)) {
        const m = val as dayjs.Dayjs;
        res = LocalDate.of(m.year(), m.month() + 1, m.date());
      } else if (val instanceof Date) {
        res = LocalDate.of(
          val.getFullYear(),
          val.getMonth() + 1,
          val.getDate(),
        );
      } else if (val instanceof ZonedDateTime) {
        res = LocalDate.of(val.year(), val.monthValue(), val.dayOfMonth());
      } else {
        console.warn('Invalid type passed into toJSDate: ', val);
      }
    }
    return res;
  }

  /**
   * Usable Date
   * Converts the given string to a joda LocalDate, if the year is in the given range.
   * This is for converting strings from a form date input that gets updated each time a character is typed.
   * @param val
   * @param minYear
   * @param maxYear
   */
  static usableDate(
    val: string,
    minYear = 1000,
    maxYear = 3000,
  ): LocalDate | undefined {
    let res: LocalDate | undefined;
    if (val && val.length >= 10) {
      let ld: LocalDate | undefined;
      try {
        if (val.length > 10) {
          val = val.substr(0, 10);
        }
        ld = LocalDate.parse(val as string);
      } catch (e) {
        return undefined;
      }
      if (ld) {
        const year = ld.year();
        if (year >= minYear && year < maxYear) {
          res = ld;
        }
      }
    }
    return res;
  }

  /**
   * To DateTime
   * Converts the given val to a joda ZonedDateTime.
   * If val is a LocalDate, or string of length 10, then the time part is set to midnight, in the local timezone.
   * @param val
   * @param zoneId
   */
  static toDateTime(
    val: unknown,
    zoneId = ZoneId.systemDefault(),
  ): ZonedDateTime | undefined {
    let res: ZonedDateTime | undefined;
    if (val) {
      if (val instanceof ZonedDateTime) {
        res = val;
      } else if (typeof val === 'string') {
        try {
          if (val.length === 10) {
            const ld = LocalDate.parse(val);
            res = ZonedDateTime.of(
              ld,
              LocalTime.MIDNIGHT,
              zoneId,
            );
          } else {
            res = ZonedDateTime.parse(val);
          }
        } catch (e) {
          console.error('Error parsing Date for: ', val);
          console.warn((e as Error).toString());
        }
      } else if (dayjs.isDayjs(val)) {
        const m = val as dayjs.Dayjs;
        res = ZonedDateTime.of(
          m.year(),
          m.month() + 1,
          m.date(),
          m.hour(),
          m.minute(),
          m.second(),
          m.millisecond() * 1000,
          ZoneOffset.ofTotalMinutes(m.utcOffset()),
        );
      } else if (val instanceof Date) {
        const offset = -val.getTimezoneOffset(); // thankyou JS for making this the opposite of what you would expect....
        res = ZonedDateTime.of(
          val.getFullYear(),
          val.getMonth() + 1,
          val.getDate(),
          val.getHours(),
          val.getMinutes(),
          val.getSeconds(),
          val.getMilliseconds() * 1000,
          ZoneOffset.ofTotalMinutes(offset),
        );
      } else if (val instanceof LocalDate) {
        res = ZonedDateTime.of(val, LocalTime.MIDNIGHT, zoneId);
      } else {
        console.warn('Invalid type passed into toJSDate: ', val);
      }
      // Eliminate timezone info, just want the offset
      if (res) {
        res = res.withFixedOffsetZone();
      }
    }
    return res;
  }

  /**
   * To Dayjs
   * Attempts to convert the given val to a Dayjs.
   * Enhances the base toDayJs by adding the ability to hand ZonedDateTime and LocalDate
   * @param val
   * @param dateFormat
   */
  static override toDayjs(
    val: unknown,
    dateFormat?: string,
  ): dayjs.Dayjs | undefined {
    let res: dayjs.Dayjs | undefined;
    if (val) {
      if (val instanceof LocalDate) {
        res = dayjs(
          new Date(val.year(), val.monthValue() - 1, val.dayOfMonth(), 0, 0, 0),
        );
      } else if (val instanceof ZonedDateTime) {
        const d = convert(val).toDate();
        res = dayjs(d);
        //res = dayjs({year: val.year(), month: val.monthValue() - 1, day: val.dayOfMonth(), hour: val.hour(), minutes: val.minute(), second:val.second(), millisecond: val.nano() / 1000});
      } else if (dayjs.isDayjs(val)) {
        res = val;
      } else if (val instanceof Date) {
        res = dayjs(val);
      } else if (typeof val === 'string') {
        try {
          res = dayjs(val, dateFormat);
        } catch (e) {
          console.error('Error parsing Date for: ', val);
          if (e instanceof Error) {
            console.warn(e.toString());
          }
        }
      } else {
        console.warn('Invalid type passed into toDayjs');
      }
      // If we didn't get a valid dayjs, set to undefined
      if (res && !res.isValid()) {
        res = undefined;
      }
    }
    return res;
  }

  /**
   * Compare
   * Compares two dates, and returns the standard -1/0/1.
   * If both dates are null/undefined, will return 0.
   * If one is not null, returns the side that is not null
   * Enhances the base DayJS version to handle Joda LocalDate/ZonedDateTime
   * @param d1
   * @param d2
   */
  static override compare(
    d1: unknown | undefined,
    d2: unknown | undefined,
  ): number {
    let res = 0;
    let dt1: ZonedDateTime | undefined;
    let dt2: ZonedDateTime | undefined;

    if (d1) {
      if (d1 instanceof ZonedDateTime) {
        dt1 = d1.withFixedOffsetZone();
      } else {
        dt1 = this.toDateTime(d1);
      }
    }

    if (d2) {
      if (d2 instanceof ZonedDateTime) {
        dt2 = d2.withFixedOffsetZone();
      } else {
        dt2 = this.toDateTime(d2);
      }
    }

    // If both are undefined, then we treat them as equal
    if (!d1 && !d2) {
      return 0;
    } else if (!d1) {
      return -1;
    } else if (!d2) {
      return 1;
    }

    // If both are defined, then just do a simple comparison
    if (dt1 && dt2) {
      // Joda does some weird stuff with timezones, we just care about the instant in time, so just compare those.
      res = dt1.toEpochSecond() - dt2.toEpochSecond();
    }
    return res;
  }

  static toFormInputDateTime(
    dt: ZonedDateTime | LocalDateTime | undefined | null,
  ): string {
    let dString = '';
    if (dt) {
      const iso = this.toIsoDateStr(dt);
      if (iso && iso.length > 19) {
        dString = iso.slice(0, 19);
      }
    }
    return dString;
  }
}
